import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { config } from '../../config/env';

const SupabaseTest: React.FC = () => {
  const [status, setStatus] = useState<string>('Testing...');
  const [error, setError] = useState<string>('');
  const [credentials, setCredentials] = useState<any>({});
  const [testResults, setTestResults] = useState<any>({});

  useEffect(() => {
    testConnection();
  }, []);

  const testConnection = async () => {
    try {
      setStatus('Testing connection...');
      setError('');

      // Verificar configurações
      const currentConfig = {
        url: config.supabase.url,
        key: config.supabase.anonKey,
        hasUrl: !!config.supabase.url,
        hasKey: !!config.supabase.anonKey,
        keyLength: config.supabase.anonKey?.length || 0,
        keyStartsWith: config.supabase.anonKey?.substring(0, 20) || 'N/A'
      };

      setCredentials(currentConfig);

      // Testar conexão básica
      const { data, error } = await supabase.from('users').select('count').limit(1);

      if (error) {
        throw error;
      }

      setStatus('✅ Supabase connection working!');
      setTestResults((prev: Record<string, any>) => ({ ...prev, connection: 'success' }));
    } catch (err: any) {
      console.error('Connection error:', err);
      setError(err.message || 'Unknown error');
      setStatus('❌ Connection error');
      setTestResults((prev: Record<string, any>) => ({ ...prev, connection: 'error', errorDetails: err }));
    }
  };

  const testAuth = async () => {
    try {
      setStatus('Testing authentication...');
      setError('');

      const { data, error } = await supabase.auth.getSession();

      if (error) {
        throw error;
      }

      setStatus('✅ Authentication working!');
      setTestResults((prev: Record<string, any>) => ({ ...prev, auth: 'success' }));
    } catch (err: any) {
      console.error('Authentication error:', err);
      setError(err.message || 'Unknown error');
      setStatus('❌ Authentication error');
      setTestResults((prev: Record<string, any>) => ({ ...prev, auth: 'error', authErrorDetails: err }));
    }
  };

  const testSignUp = async () => {
    try {
      setStatus('Testing signup...');
      setError('');

      const testEmail = `test-${Date.now()}@example.com`;
      const { data, error } = await supabase.auth.signUp({
        email: testEmail,
        password: 'test123456',
        options: {
          data: {
            name: 'Test User',
            phone: '123456789'
          }
        }
      });

      if (error) {
        throw error;
      }

      setStatus('✅ Signup working!');
      setTestResults((prev: Record<string, any>) => ({ ...prev, signup: 'success' }));
    } catch (err: any) {
      console.error('Signup error:', err);
      setError(err.message || 'Unknown error');
      setStatus('❌ Signup error');
      setTestResults((prev: Record<string, any>) => ({ ...prev, signup: 'error', signupErrorDetails: err }));
    }
  };

  const showEnvironmentInfo = () => {
    console.log('Environment Info:', {
      NODE_ENV: import.meta.env.NODE_ENV,
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 20) + '...',
      config: config
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-4">🔧 Supabase Connection Test</h2>
      
      <div className="space-y-4">
        {/* Status */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">Status:</h3>
          <p className="text-lg">{status}</p>
          {error && (
            <p className="text-red-600 mt-2 text-sm">{error}</p>
          )}
        </div>

        {/* Credentials */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold mb-2">Settings:</h3>
          <div className="space-y-2 text-sm">
            <p><strong>URL:</strong> {credentials.hasUrl ? '✅ Set' : '❌ Not set'}</p>
            <p><strong>Key:</strong> {credentials.hasKey ? '✅ Set' : '❌ Not set'}</p>
            <p><strong>Key length:</strong> {credentials.keyLength} chars</p>
            <p><strong>Key starts with:</strong> <code className="bg-gray-200 px-1 rounded">{credentials.keyStartsWith}</code></p>
            {credentials.url && (
              <p><strong>Current URL:</strong> <code className="bg-gray-200 px-1 rounded">{credentials.url}</code></p>
            )}
          </div>
        </div>

        {/* Test Results */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h3 className="font-semibold mb-2">Test Results:</h3>
          <div className="space-y-2 text-sm">
            <p><strong>Connection:</strong> {testResults.connection === 'success' ? '✅ Success' : testResults.connection === 'error' ? '❌ Error' : '⏳ Not tested'}</p>
            <p><strong>Authentication:</strong> {testResults.auth === 'success' ? '✅ Success' : testResults.auth === 'error' ? '❌ Error' : '⏳ Not tested'}</p>
            <p><strong>Signup:</strong> {testResults.signup === 'success' ? '✅ Success' : testResults.signup === 'error' ? '❌ Error' : '⏳ Not tested'}</p>
          </div>
        </div>

        {/* Test Buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={testConnection}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Test Connection
          </button>
          <button
            onClick={testAuth}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Test Authentication
          </button>
          <button
            onClick={testSignUp}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            Test Signup
          </button>
          <button
            onClick={showEnvironmentInfo}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Show Info in Console
          </button>
        </div>

        {/* Instructions */}
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <h3 className="font-semibold mb-2 text-yellow-800">📋 Instructions:</h3>
          <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-700">
            <li>Make sure credentials are configured on Vercel</li>
            <li>Ensure your Supabase project is active</li>
            <li>Run the setup SQL in Supabase</li>
            <li>Redeploy on Vercel after setting environment variables</li>
            <li>Click "Show Info in Console" and check the logs</li>
          </ol>
        </div>

        {/* Debug Info */}
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h3 className="font-semibold mb-2 text-red-800">🐛 Debug Info:</h3>
          <div className="text-sm text-red-700">
            <p><strong>NODE_ENV:</strong> {import.meta.env.NODE_ENV}</p>
            <p><strong>VITE_SUPABASE_URL:</strong> {import.meta.env.VITE_SUPABASE_URL ? '✅ Set' : '❌ Not set'}</p>
            <p><strong>VITE_SUPABASE_ANON_KEY:</strong> {import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Set' : '❌ Not set'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupabaseTest;